import React, { useState } from 'react';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
  Button,
  FormControl,
  Select,
  MenuItem,
  SelectChangeEvent,
  Box,
  Switch,
  FormControlLabel
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import InfoIcon from '@mui/icons-material/Info';
import WarningIcon from '@mui/icons-material/Warning';
import {
  STATE_DRAFT,
  STATE_INTERNAL_PUBLISHED,
  STATE_PUBLISHED,
  TERMS_AND_CONDITIONS_URL
} from '@opengov/stories-shared/constants';

import { IStoryListMessages } from '../../../../types';
import { useBulkStatusChange } from '../../Publications/PublicationView/hooks/usePublicationMutations';
import { useStoriesSelectionContext } from '../../Publications/PublicationView/context/StoriesSelectionContext';
import { getStoryStatusDisplayName } from '../../../utils';
import { styles } from './ChangeStatusModal.styles';

export interface IChangeStatusModalProps {
  messages: IStoryListMessages;
  selectedStoriesIds: string[];
  publicationId: string;
  entityId: number;
  entityUuid: string;
  selectAllStories: boolean;
  sectionId: string;
  searchText: string;
  storyState: string;
  show: string;
  canFollowStories?: boolean;
  onClose(): void;
  onSuccess?(): void;
}

export const ChangeStatusModal: React.FC<IChangeStatusModalProps> = (props: IChangeStatusModalProps) => {
  const {
    messages,
    publicationId,
    entityId,
    entityUuid,
    selectedStoriesIds,
    selectAllStories,
    sectionId,
    searchText,
    storyState,
    show,
    canFollowStories,
    onClose,
    onSuccess
  } = props;

  const selectionCtx = useStoriesSelectionContext();
  const bulkStatusChangeMutation = useBulkStatusChange({
    title: messages.somethingWentWrongError,
    body: messages.storyStatusChangeFailed
  });
  const [selectChangeStatus, setSelectChangeStatus] = useState<string>('');
  const [allowFollow, setAllowFollow] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const handleBulkStatusChange = async () => {
    setIsLoading(true);
    try {
      const bulkChangeStatusJobId = await bulkStatusChangeMutation.mutateAsync({
        publicationId: publicationId,
        entityId: entityId,
        entityUuid: entityUuid,
        selectedStoriesIds: selectedStoriesIds,
        selectChangeStatus: selectChangeStatus,
        selectAllStories: selectAllStories,
        sectionId: sectionId,
        searchText: searchText,
        storyState: storyState,
        show: show,
        isFollowable: allowFollow
      });
      onClose();
      selectionCtx.setBulkChangeStatusJobId(bulkChangeStatusJobId);
      selectionCtx.clearSelection();
      onSuccess?.();
    } catch {
      // The mutation's onError already toasts. Swallow here so the rejection
      // isn't left floating, and fall through to re-enable the button.
    } finally {
      setIsLoading(false);
    }
  };

  const handleStatusChange = (event: SelectChangeEvent<string>) => {
    setSelectChangeStatus(event.target.value);
  };

  const onFollowToggle = (event: React.ChangeEvent<HTMLInputElement>) => {
    const isChecked = event.target.checked;
    setAllowFollow(isChecked);
  };

  return (
    <Dialog onClose={onClose} open={true} fullWidth maxWidth="sm">
      <DialogTitle>
        {messages.changeStatus}
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={styles.closeButton}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      
      <DialogContent>
        <Typography variant="body1" sx={styles.descriptionText}>
          {messages.changeStatusDescription}
        </Typography>
        
        <Typography id="change-status-visibility-label" variant="body2" sx={styles.visibilityLabel}>
          {messages.visibility}
        </Typography>

        <FormControl fullWidth size="small">
          <Select
            data-test="change-status-dropdown"
            name="status"
            value={selectChangeStatus}
            onChange={handleStatusChange}
            displayEmpty
            notched={false}
            renderValue={(selected) => getStoryStatusDisplayName(selected)}
            inputProps={{ 'aria-labelledby': 'change-status-visibility-label', className: 'no-format' }}
          >
            <MenuItem value={STATE_DRAFT} sx={styles.menuItem}>
              <Typography variant="body1">Draft</Typography>
              <Typography variant="body2" color="text.secondary">
                {messages.onlyAccessibleByOwner}
              </Typography>
            </MenuItem>
            <MenuItem value={STATE_INTERNAL_PUBLISHED} sx={styles.menuItem}>
              <Typography variant="body1">Internal</Typography>
              <Typography variant="body2" color="text.secondary">
                {messages.shareWithMembersOfYourOrganization}
              </Typography>
            </MenuItem>
            <MenuItem value={STATE_PUBLISHED} sx={styles.menuItem}>
              <Typography variant="body1">Public</Typography>
              <Typography variant="body2" color="text.secondary">
                {messages.anyoneWithTheLinkCanView}
              </Typography>
            </MenuItem>
          </Select>
        </FormControl>

        {selectChangeStatus && (
          <Box sx={styles.warningContainer} data-test="copy-without-status-message">
            <WarningIcon sx={styles.warningIcon} />
            <Typography variant="body2" sx={styles.warningText}>
              {messages.overrideOnlinePublicationSettings}
            </Typography>
          </Box>
        )}

        {selectChangeStatus === STATE_PUBLISHED && canFollowStories && (
         <>
         <FormControlLabel
            control={
              <Switch
                size="small"
                checked={allowFollow}
                onChange={onFollowToggle}
                color="primary"
                inputProps={styles.switchInputProps}
              />
            }
            label={messages.allowViewersToFollowForUpdates}
            labelPlacement="start"
            sx={styles.followFormControl}
          />
           {allowFollow ? (
            <Box sx={styles.disclaimerContainer}>
              <Box sx={styles.disclaimerBox}>
                <InfoIcon />
              <Typography  sx={styles.disclaimerText}>
                {messages.followAndCommentDesclaimer}
                <Typography  sx={{...styles.disclaimerText,...styles.termsAndConditions}}>
                <a href={TERMS_AND_CONDITIONS_URL} target="_blank" rel="noopener noreferrer">{messages.termsAndConditions}</a>
              </Typography>
              </Typography>
              
              </Box>
            </Box>) : null} 
          </>
        )}
      </DialogContent>

      <DialogActions sx={styles.dialogActions}>
        <Button onClick={onClose} data-test="button-cancel" variant="text">
          {messages.cancel}
        </Button>
        <Button 
          onClick={handleBulkStatusChange}
          data-test="button-save"
          variant="contained"
          disabled={!selectChangeStatus || isLoading}
        >
          {isLoading ? 'Changing...' : 'Change'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
